import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SearchInput } from './search-input';
import { SelectColumns } from './columns-setting';
import { ColumnFilterDropdown } from './column-filter-dropdown';
import { utilService } from '../../services/util-service';

interface GenericTableProps {
  data: any[][];
  recordName?: string;
  searchPlaceholder?: string;
  renderExpandedRow?: (rowData: any[], rowIndex: number) => React.ReactNode;
  onCellClick?: (rowData: any[], rowIndex: number, cellTitle: string) => void;
  onEditClick?: (rowData: any[]) => void;
  onCloneClick?: (rowData: any[]) => void;
  editColumn?: string;
  enableRowSelection?: boolean;
  selectedRows?: Set<number>;
  onSelectionChange?: (selectedIndices: Set<number>) => void;
  filterableColumns?: string[];
  clickableCells?: string[];
  hiddenByDefault?: string[];
  alwaysVisible?: string[];
  defaultSort?: { key: string; direction: 'asc' | 'desc' };
  getRowClassName?: (rowData: any[]) => string;
}

const getCellClassName = (title: string) => {
  const key = title.toLowerCase().replace(/[^a-z0-9]/g, '-')
  return `cell-${key}`
}

const IconEyeOpen = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const IconEyeClosed = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
)

const IconCheckCircle = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
)

const IconMinusCircle = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9e9e9e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
)

const IconPencil = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

const IconColumns = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3h18v18H3z" />
    <line x1="9" y1="3" x2="9" y2="21" />
    <line x1="15" y1="3" x2="15" y2="21" />
  </svg>
)

const PartialIcon: React.FC<{ tooltip: string }> = ({ tooltip }) => (
  <span className="partial-indicator" title={tooltip}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#e65100" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  </span>
)

const renderCellContent = (cellData: any) => {
  const title = cellData.title
  const value = cellData.value
  const partialTooltip = cellData.partialTooltip

  if (cellData.render) {
    return cellData.render
  }

  if (title === 'Status' && typeof value === 'string' && value) {
    const lower = value.toLowerCase().trim()
    let statusClass = 'status-default'
    if (['passed', 'pass', 'completed', 'on', 'active', 'success', 'approved'].includes(lower)) statusClass = 'status-passed'
    else if (['failed', 'fail', 'error', 'exception', 'rejected'].includes(lower)) statusClass = 'status-failed'
    else if (['pending', 'in progress', 'in_progress', 'running', 'processing', 'waiting'].includes(lower)) statusClass = 'status-pending'
    else if (['not started', 'not_started', 'new', 'draft', 'created'].includes(lower)) statusClass = 'status-not-started'
    else if (['off', 'inactive', 'disabled', 'paused'].includes(lower)) statusClass = 'status-inactive'

    return (
      <span className={`status-badge ${statusClass}`}>
        {value}
        {partialTooltip && <> <PartialIcon tooltip={partialTooltip} /></>}
      </span>
    )
  }

  if (cellData.type === 'cognito') {
    if (!value) return <span className="cell-empty" title="User does not exist in AWS Cognito">—</span>
    const enabled = cellData.cognitoEnabled === 'Yes'
    const statusDescriptions: Record<string, string> = {
      'CONFIRMED': 'User confirmed their account and can sign in',
      'UNCONFIRMED': 'User registered but has not confirmed their account',
      'FORCE_CHANGE_PASSWORD': 'User must change their password on next sign-in',
      'COMPROMISED': 'User account has been marked as compromised',
      'RESET_REQUIRED': 'User must reset their password before signing in',
      'ARCHIVED': 'User account has been archived',
      'UNKNOWN': 'User status is unknown',
      'EXTERNAL_PROVIDER': 'User signed up through an external identity provider',
    }
    const desc = statusDescriptions[value] || value
    const enabledText = cellData.cognitoEnabled && cellData.cognitoEnabled !== '—' ? `\nEnabled: ${cellData.cognitoEnabled}` : ''
    const tooltip = `${value}\n${desc}${enabledText}`
    return (
      <span className={`cognito-indicator ${enabled ? 'cognito-active' : 'cognito-disabled'}`} title={tooltip}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
          {!enabled && <>
            <line x1="4" y1="4" x2="20" y2="20" stroke="#c62828" strokeWidth="2.5" />
          </>}
        </svg>
      </span>
    )
  }

  if (title === 'DB Roles' && typeof value === 'string' && value && value !== '—') {
    const roles = value.split(', ')
    return (
      <span className="role-badges">
        {roles.map((role, i) => {
          const r = role.trim().toLowerCase()
          let cls = 'role-badge-default'
          if (r === 'admin') cls = 'role-badge-admin'
          else if (r === 'admin-read') cls = 'role-badge-admin-read'
          else if (r === 'control-owner') cls = 'role-badge-control-owner'
          else if (r === 'ac-write') cls = 'role-badge-ac-write'
          else if (r === 'ac-read') cls = 'role-badge-ac-read'
          return <span key={i} className={`role-badge ${cls}`}>{role}</span>
        })}
      </span>
    )
  }

  if (title === 'Visible') {
    const isVisible = value === 'Yes'
    return (
      <span className={`icon-cell ${isVisible ? 'icon-active' : 'icon-muted'}`}>
        {isVisible ? <IconEyeOpen /> : <IconEyeClosed />}
      </span>
    )
  }

  if (title === 'Active') {
    const isActive = value === 'Yes'
    return (
      <span className="icon-cell">
        {isActive ? <IconCheckCircle /> : <IconMinusCircle />}
      </span>
    )
  }

  if (title === 'Key Objective') {
    const isKey = value === 'Yes'
    return (
      <span className="icon-cell">
        {isKey ? <IconCheckCircle /> : <IconMinusCircle />}
      </span>
    )
  }

  if (title === 'Actions') {
    return null // Handled inline by GenericTable for expandable support
  }

  if (title === 'ID' && cellData.manualStatus?.isManual) {
    return (
      <span className="id-manual-wrap">
        <span>{value}</span>
        <span className="id-manual-badge" title="Manual control - at least one linked entity system is not required daily">
          Manual
        </span>
      </span>
    )
  }

  // Format JSON — always pretty-printed
  if (cellData.type === 'json') {
    if (value == null || value === '') return <span className="cell-empty">—</span>
    let parsed = value
    if (typeof value === 'string') {
      try { parsed = JSON.parse(value) } catch { return value }
    }
    if (Array.isArray(parsed) && parsed.length === 0) return <span className="cell-empty">—</span>
    if (typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length === 0) return <span className="cell-empty">—</span>
    return <pre className="cell-json">{JSON.stringify(parsed, null, 4)}</pre>
  }

  // Format arrays — comma-separated for simple values, JSON for objects
  if (cellData.type === 'array' && Array.isArray(value)) {
    if (value.length === 0) return <span className="cell-empty">—</span>
    const hasObjects = value.some(item => typeof item === 'object' && item !== null)
    if (hasObjects) {
      return <pre className="cell-json">{JSON.stringify(value, null, 4)}</pre>
    }
    return value.join(', ')
  }

  // Format objects — pretty-printed JSON
  if (cellData.type === 'object' && value && typeof value === 'object') {
    return <pre className="cell-json">{JSON.stringify(value, null, 4)}</pre>
  }

  // Format datetime using shared formatter
  if (cellData.type === 'datetime' && typeof value === 'string' && value && value !== 'Never') {
    const formatted = utilService.formatDateTime(value)
    if (formatted) return formatted
  }

  // Health status badge (green/orange/red dot + label)
  if (cellData.type === 'health' && value && typeof value === 'object' && value.severity) {
    const colorMap: Record<string, string> = { green: '#2e7d32', orange: '#e65100', red: '#c62828' }
    const bgMap: Record<string, string> = { green: '#f0fdf4', orange: '#fff3e0', red: '#fef2f2' }
    const borderMap: Record<string, string> = { green: '#bbf7d0', orange: '#ffe0b2', red: '#fecaca' }
    return (
      <span className="health-badge" title={value.tooltip || ''} style={{ display: 'inline-flex', alignItems: 'center', padding: '0.2rem 0.55rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600, color: colorMap[value.severity], backgroundColor: bgMap[value.severity], border: `1px solid ${borderMap[value.severity]}`, cursor: 'default' }}>
        {value.label}
      </span>
    )
  }

  if (partialTooltip) {
    return <>{value} <PartialIcon tooltip={partialTooltip} /></>
  }

  return value
}

const IconChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

const BLANKS_VALUE = '(Blanks)'

const CLICKABLE_CELLS = ['Visible', 'Active']

const FILTER_PARAM_PREFIX = 'filter_';

const getFilterParamKey = (columnTitle: string) =>
  FILTER_PARAM_PREFIX + columnTitle.toLowerCase().replace(/\s+/g, '-');

const GenericTable: React.FC<GenericTableProps> = ({ data, recordName = 'Records', searchPlaceholder = 'Search...', renderExpandedRow, onCellClick, onEditClick, onCloneClick, editColumn = 'ID', filterableColumns, clickableCells, hiddenByDefault, alwaysVisible, defaultSort, getRowClassName, enableRowSelection, selectedRows: externalSelectedRows, onSelectionChange }) => {
  const clickableCellSet = clickableCells ?? CLICKABLE_CELLS
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSelectFieldsVisible, setIsSelectFieldsVisible] = useState(false);
  const selectedRows = externalSelectedRows ?? new Set<number>()

  const toggleRowSelection = (rowIndex: number) => {
    const next = new Set(selectedRows)
    if (next.has(rowIndex)) next.delete(rowIndex)
    else next.add(rowIndex)
    onSelectionChange?.(next)
  }

  const toggleSelectAll = (filteredCount: number, allIndices: number[]) => {
    const next = selectedRows.size === filteredCount ? new Set<number>() : new Set(allIndices)
    onSelectionChange?.(next)
  }

  const searchTerm = searchParams.get('search') || '';
  const sortKey = searchParams.get('sort') || defaultSort?.key || null;
  const sortDirection = searchParams.get('dir') || (searchParams.get('sort') ? '' : defaultSort?.direction || '');
  const expandedRowIndex = searchParams.has('expanded') ? Number(searchParams.get('expanded')) : null;
  const sortConfig = { key: sortKey, direction: sortDirection };

  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  };

  // Read column filters from URL params
  const columnFilters: Record<string, string[]> = useMemo(() => {
    const filters: Record<string, string[]> = {};
    if (!filterableColumns) return filters;
    filterableColumns.forEach(col => {
      const paramVal = searchParams.get(getFilterParamKey(col));
      if (paramVal === '__none__') {
        filters[col] = [];
      } else if (paramVal) {
        filters[col] = paramVal.split(',');
      }
    });
    return filters;
  }, [searchParams, filterableColumns]);

  const handleColumnFilterChange = (columnTitle: string, values: string[]) => {
    const paramKey = getFilterParamKey(columnTitle);
    const allValues = cascadingColumnValues[columnTitle] || [];
    // Only remove param when all values are selected (no filtering needed)
    if (values.length === allValues.length) {
      updateParams({ [paramKey]: null });
    } else {
      // Use __none__ sentinel for "nothing selected" since updateParams strips empty strings
      updateParams({ [paramKey]: values.length === 0 ? '__none__' : values.join(',') });
    }
  };
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const isResizingRef = useRef(false);
  const tableRef = useRef<HTMLTableElement>(null);

  const columnsInitialized = useRef(false)
  useEffect(() => {
    if (data && data[0] && !columnsInitialized.current) {
      const hiddenSet = new Set(hiddenByDefault || [])
      const alwaysSet = new Set(alwaysVisible || [])
      setSelectedColumns(data[0].map((column: any) => column.title).filter((t: string) => !hiddenSet.has(t) || alwaysSet.has(t)))
      columnsInitialized.current = true
    }
  }, [data, hiddenByDefault, alwaysVisible])

  // Measure header text to compute initial column widths
  // Compute column widths so all columns fit within the container
  useEffect(() => {
    if (!tableRef.current || !data || !data[0]) return
    const container = tableRef.current.parentElement
    if (!container) return

    const cols = data[0]
      .filter((col: any) => selectedColumns.includes(col.title))
      .map((col: any) => col.title)

    if (cols.length === 0) return

    const canvas = document.createElement('canvas')
    let ctx: CanvasRenderingContext2D | null = null
    try {
      ctx = canvas.getContext('2d')
    } catch {
      return
    }
    if (!ctx) return
    const measureCtx = ctx

    const filterIconSpace = 18 // space for inline filter icon
    const padding = 40 // cell padding + resize handle + letter-spacing buffer

    // Detect narrow columns by type from the first data row
    const colTypes: Record<string, string> = {}
    if (data[0]) {
      data[0].forEach((col: any) => { colTypes[col.title] = col.type })
    }
    const isNarrowCol = (title: string) => {
      const type = colTypes[title]
      const lower = title.toLowerCase()
      return type === 'boolean' || type === 'actions' || type === 'int' || type === 'datetime' || type === 'cognito'
        || lower.includes('id') || lower === 'status' || lower === 'visible' || lower === 'active'
    }

    // Absolute minimum: header text must always be fully visible
    const headerTextWidths: Record<string, number> = {}
    measureCtx.font = '600 0.72rem Montserrat, sans-serif'
    cols.forEach((title: string) => {
      const extraForFilter = filterableColumns?.includes(title) ? filterIconSpace : 0
      headerTextWidths[title] = Math.ceil(measureCtx.measureText(title.toUpperCase()).width + padding + extraForFilter)
    })

    // Ideal widths: header text + extra content space for certain column types
    const idealWidths: Record<string, number> = {}
    cols.forEach((title: string) => {
      let contentMin = 0
      const type = colTypes[title]
      const lower = title.toLowerCase()
      if (lower === 'id' || (lower.endsWith(' id') && (type === 'int' || type === 'string'))) {
        measureCtx.font = '0.8rem Montserrat, sans-serif'
        contentMin = measureCtx.measureText('000000').width
        measureCtx.font = '600 0.72rem Montserrat, sans-serif'
      }
      if (lower === 'status') {
        measureCtx.font = '0.75rem Montserrat, sans-serif'
        contentMin = measureCtx.measureText('Pending Approval').width + 36
        measureCtx.font = '600 0.72rem Montserrat, sans-serif'
      }
      idealWidths[title] = Math.ceil(Math.max(headerTextWidths[title], contentMin + padding))
    })

    const tableWidth = container.clientWidth
    const totalHeaderMin = cols.reduce((sum: number, t: string) => sum + headerTextWidths[t], 0)
    const totalIdealWidth = cols.reduce((sum: number, t: string) => sum + idealWidths[t], 0)

    const widths: Record<string, number> = {}

    if (totalHeaderMin >= tableWidth) {
      // Even headers alone exceed the table — scale proportionally to fit
      cols.forEach((title: string) => {
        widths[title] = Math.floor(headerTextWidths[title] * (tableWidth / totalHeaderMin))
      })
    } else if (totalIdealWidth > tableWidth) {
      // Headers fit but ideal widths don't — give headers their min, distribute remaining proportionally
      const extraAvailable = tableWidth - totalHeaderMin
      const totalExtra = totalIdealWidth - totalHeaderMin
      cols.forEach((title: string) => {
        const colExtra = idealWidths[title] - headerTextWidths[title]
        widths[title] = headerTextWidths[title] + Math.floor(colExtra * (extraAvailable / totalExtra))
      })
    } else {
      // Everything fits — distribute leftover space to flex (non-narrow) columns
      const flexCols = cols.filter((t: string) => !isNarrowCol(t))
      const narrowTotal = cols
        .filter((t: string) => isNarrowCol(t))
        .reduce((sum: number, t: string) => sum + idealWidths[t], 0)
      const flexMinTotal = flexCols.reduce((sum: number, t: string) => sum + idealWidths[t], 0)
      const remainingWidth = tableWidth - narrowTotal
      const extraSpace = Math.max(0, remainingWidth - flexMinTotal)
      const extraPerCol = flexCols.length > 0 ? Math.floor(extraSpace / flexCols.length) : 0

      cols.forEach((title: string) => {
        if (isNarrowCol(title)) {
          widths[title] = idealWidths[title]
        } else {
          widths[title] = idealWidths[title] + extraPerCol
        }
      })
    }

    setColumnWidths(widths)
  }, [data, selectedColumns, filterableColumns])

  const onResizeStart = useCallback((e: React.MouseEvent, colKey: string) => {
    e.preventDefault()
    e.stopPropagation()
    const th = (e.target as HTMLElement).parentElement
    if (!th) return

    isResizingRef.current = true

    const startX = e.clientX
    const startWidth = th.offsetWidth

    const onMouseMove = (moveEvent: MouseEvent) => {
      const diff = moveEvent.clientX - startX
      const newWidth = Math.max(40, startWidth + diff)
      setColumnWidths(prev => ({ ...prev, [colKey]: newWidth }))
    }

    const onMouseUp = () => {
      setTimeout(() => { isResizingRef.current = false }, 0)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [])


  const handleSort = (key: string) => {
    if (isResizingRef.current) return
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    updateParams({ sort: key, dir: direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return data;
    const key = sortConfig.key;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const baseSorted = [...data].sort((a, b) => {
      const aCell = a.find((c: any) => c.title === key)
      const bCell = b.find((c: any) => c.title === key)
      let aVal = aCell?.value ?? ''
      let bVal = bCell?.value ?? ''
      // For datetime columns, treat non-date values (e.g. "Never") as earliest possible
      if (aCell?.type === 'datetime' || bCell?.type === 'datetime') {
        if (typeof aVal === 'string' && !aVal.match(/^\d/)) aVal = ''
        if (typeof bVal === 'string' && !bVal.match(/^\d/)) bVal = ''
      }
      // For health columns, sort by sortKey (0=red worst, 3=green best)
      if (aCell?.type === 'health' || bCell?.type === 'health') {
        aVal = aVal?.sortKey ?? -1
        bVal = bVal?.sortKey ?? -1
      }
      if (aVal > bVal) return dir;
      if (aVal < bVal) return -dir;

      return 0;
    });
    const nonManualRows = baseSorted.filter((row: any) => row.find((c: any) => c.title === 'ID')?.manualStatus?.isManual !== true)
    const manualRows = baseSorted.filter((row: any) => row.find((c: any) => c.title === 'ID')?.manualStatus?.isManual === true)
    return [...nonManualRows, ...manualRows];
  }, [data, sortConfig.key, sortConfig.direction]);

  const searchedData = useMemo(() => {
    if (!searchTerm) return sortedData;
    const term = searchTerm.toLowerCase();
    return sortedData.filter(row =>
      row.some((cell: any) => {
        if (cell.type === 'health' && cell.value?.label) return cell.value.label.toLowerCase().includes(term)
        return cell.value.toString().toLowerCase().includes(term)
      })
    );
  }, [sortedData, searchTerm]);

  // Cascading unique values: for each filterable column, derive values from
  // searchedData filtered by all OTHER columns' active filters (excluding own).
  // This gives Excel-style cascading: Column B's dropdown reflects Column A's filter.
  const cascadingColumnValues: Record<string, string[]> = useMemo(() => {
    if (!filterableColumns || !searchedData || searchedData.length === 0) return {};
    const filterEntries = Object.entries(columnFilters);
    const result: Record<string, string[]> = {};
    filterableColumns.forEach(col => {
      // Apply all filters EXCEPT the current column's own filter.
      // Also skip filters with empty selection — an empty allowedValues means the user
      // cleared everything and is in the process of re-selecting; it shouldn't cascade
      // to other dropdowns and empty them.
      const otherFilters = filterEntries.filter(([key, vals]) => key !== col && vals.length > 0);
      const rows = otherFilters.length === 0
        ? searchedData
        : searchedData.filter(row =>
            otherFilters.every(([filterCol, allowedValues]) => {
              const cell = row.find((c: any) => c.title === filterCol);
              if (!cell) return true
              const cv = cell.type === 'health' ? cell.value?.label : String(cell.value)
              return allowedValues.includes(cv)
            })
          );
      const valuesSet = new Set<string>();
      let hasBlanks = false;
      rows.forEach(row => {
        const cell = row.find((c: any) => c.title === col);
        if (cell && cell.value != null && cell.value !== '') {
          valuesSet.add(cell.type === 'health' ? cell.value.label : String(cell.value));
        } else {
          hasBlanks = true;
        }
      });
      const sorted = Array.from(valuesSet).sort((a, b) => a.localeCompare(b));
      if (hasBlanks) sorted.push(BLANKS_VALUE);
      result[col] = sorted;
    });
    return result;
  }, [searchedData, filterableColumns, columnFilters]);

  const filteredData = useMemo(() => {
    const filterEntries = Object.entries(columnFilters);
    if (filterEntries.length === 0) return searchedData;
    return searchedData.filter(row => {
      for (const [col, allowedValues] of filterEntries) {
        const cell = row.find((c: any) => c.title === col);
        const cellValue = (cell && cell.value != null && cell.value !== '')
          ? (cell.type === 'health' ? cell.value.label : String(cell.value))
          : BLANKS_VALUE;
        if (!allowedValues.includes(cellValue)) {
          return false;
        }
      }
      return true;
    });
  }, [searchedData, columnFilters]);

  const totalRows = filteredData.length

  // O(1) lookup from rowData → original index in `data` (used for selection that survives
  // sorting/filtering). Recomputed only when `data` reference changes.
  const dataIndexMap = useMemo(() => new Map(data.map((row: any, i: number) => [row, i])), [data])

  if (data.length === 0) {
    return (
      <div className="generic-table-empty">
        <p>No {recordName.toLowerCase()} found.</p>
      </div>
    )
  }

  const CHIP_COLORS = [
    { bg: '#e0e7ff', text: '#4338ca', border: '#c7d2fe' },
    { bg: '#f3e8ff', text: '#7c3aed', border: '#e9d5ff' },
    { bg: '#ccfbf1', text: '#0d9488', border: '#99f6e4' },
    { bg: '#fff7ed', text: '#c2410c', border: '#fed7aa' },
    { bg: '#dcfce7', text: '#15803d', border: '#bbf7d0' },
    { bg: '#fce7f3', text: '#be185d', border: '#fbcfe8' },
  ]
  const activeFilters = Object.entries(columnFilters)
  const clearAllFilters = () => {
    const removals: Record<string, null> = {}
    filterableColumns?.forEach(col => { removals[getFilterParamKey(col)] = null })
    updateParams(removals)
  }

  return (
    <div className="generic-table-container">
      <div className="table-toolbar">
        <SearchInput
          handleChange={(e: React.ChangeEvent<HTMLInputElement>) => updateParams({ search: e.target.value || null })}
          searchTerm={searchTerm}
          placeholder={searchPlaceholder}
        />
        <button
          className={`columns-toggle-button${isSelectFieldsVisible ? ' active' : ''}`}
          onClick={() => setIsSelectFieldsVisible(!isSelectFieldsVisible)}
          title="Toggle columns"
        >
          <IconColumns />
        </button>
      </div>
      {activeFilters.length > 0 && (
        <div className="active-filters-bar">
          <span className="active-filters-bar-label">Filters:</span>
          {activeFilters.map(([col, vals], i) => {
            const color = CHIP_COLORS[i % CHIP_COLORS.length]
            return (
              <span key={col} className="active-filter-chip" style={{ background: color.bg, borderColor: color.border, color: color.text }}>
                <span className="active-filter-chip-label">
                  {col}{vals.length > 0 ? `: ${vals.join(', ')}` : ': (none)'}
                </span>
                <button
                  className="active-filter-chip-remove"
                  style={{ color: color.text }}
                  onClick={() => handleColumnFilterChange(col, cascadingColumnValues[col] || [])}
                  title={`Remove ${col} filter`}
                >
                  ×
                </button>
              </span>
            )
          })}
          <button className="clear-all-filters-btn" onClick={clearAllFilters}>
            Clear all
          </button>
        </div>
      )}

      {isSelectFieldsVisible && <SelectColumns
        data={data}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        alwaysVisible={alwaysVisible}
      />}

      <div className="table-scroll-area">
      <table className="generic-table" ref={tableRef}>
        <thead>
          <tr>
            {data && data[0] && data[0].map((item: any, index: number) => {
              const isFilterable = filterableColumns?.includes(item.title);
              const hasActiveFilter = item.title in columnFilters;
              return selectedColumns.includes(item.title) && (
                <th
                  key={index}
                  className={getCellClassName(item.title)}
                  data-col={item.title}
                  style={columnWidths[item.title] ? { width: columnWidths[item.title] } : undefined}
                  onClick={() => handleSort(item.title)}
                >
                  {item.title}
                  {sortConfig.key === item.title && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? ' \u25B2' : ' \u25BC'}
                    </span>
                  )}
                  {isFilterable && (
                    <ColumnFilterDropdown
                      columnTitle={item.title}
                      uniqueValues={cascadingColumnValues[item.title] || []}
                      selectedValues={item.title in columnFilters ? columnFilters[item.title] : cascadingColumnValues[item.title] || []}
                      onFilterChange={handleColumnFilterChange}
                      isActive={hasActiveFilter}
                    />
                  )}
                  <div
                    className="resize-handle"
                    onMouseDown={(e) => onResizeStart(e, item.title)}
                  />
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {filteredData.map((rowData: any, rowIndex: number) => {
            const isExpanded = expandedRowIndex === rowIndex
            const visibleColCount = selectedColumns.length
            // Map the rendered row back to its index in the original `data` prop so selection
            // works correctly when the parent uses `data[idx]` to look up the source object.
            const originalIndex = dataIndexMap.get(rowData) ?? -1
            const handleToggle = () => {
              if (!renderExpandedRow) return
              updateParams({ expanded: isExpanded ? null : String(rowIndex) })
            }
            return (
              <React.Fragment key={rowIndex}>
                <tr
                  className={`${isExpanded ? 'row-expanded' : ''}${selectedRows.has(originalIndex) ? ' row-selected' : ''}${getRowClassName ? ` ${getRowClassName(rowData)}` : ''}`}
                  onClick={renderExpandedRow ? handleToggle : undefined}
                  style={renderExpandedRow ? { cursor: 'pointer' } : undefined}
                >
                  {rowData.map((cellData: any, cellIndex: number) => {
                    const isClickableCell = onCellClick && clickableCellSet.includes(cellData.title)
                    const isEditableCell = cellData.title === editColumn && onEditClick
                    return selectedColumns.includes(cellData.title) && (
                      <td
                        key={cellIndex}
                        className={`${getCellClassName(cellData.title)}${isClickableCell ? ' cell-clickable' : ''}${isEditableCell ? ' cell-editable' : ''}`}
                        onClick={isClickableCell ? (e) => {
                          e.stopPropagation()
                          onCellClick(rowData, rowIndex, cellData.title)
                        } : undefined}
                      >
                        {cellData.title === 'Actions' && renderExpandedRow ? (
                          <span className={`icon-cell icon-actions ${isExpanded ? 'chevron-expanded' : ''}`}>
                            <IconChevronDown />
                          </span>
                        ) : isEditableCell ? (
                          <span className="cell-edit-wrapper">
                            {renderCellContent(cellData)}
                            <span className="edit-overlay">
                              <span className="edit-overlay-btn" onClick={(e) => { e.stopPropagation(); onEditClick(rowData) }} title="Edit">
                                <IconPencil />
                              </span>
                              {onCloneClick && <span className="edit-overlay-btn clone-btn" onClick={(e) => { e.stopPropagation(); onCloneClick(rowData) }} title="Clone">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </svg>
                              </span>}
                              {enableRowSelection && <span
                                className={`edit-overlay-btn select-btn${selectedRows.has(originalIndex) ? ' selected' : ''}`}
                                onClick={(e) => { e.stopPropagation(); toggleRowSelection(originalIndex) }}
                                title={selectedRows.has(originalIndex) ? 'Deselect' : 'Select'}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill={selectedRows.has(originalIndex) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  {selectedRows.has(originalIndex)
                                    ? <><rect x="3" y="3" width="18" height="18" rx="2" /><polyline points="9 11 12 14 22 4" stroke="white" strokeWidth="2.5" /></>
                                    : <rect x="3" y="3" width="18" height="18" rx="2" />
                                  }
                                </svg>
                              </span>}
                            </span>
                          </span>
                        ) : (
                          renderCellContent(cellData)
                        )}
                      </td>
                    )
                  })}
                </tr>
                {isExpanded && renderExpandedRow && (
                  <tr className="expanded-row" onClick={e => e.stopPropagation()}>
                    <td colSpan={visibleColCount}>
                      {renderExpandedRow(rowData, rowIndex)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
      </div>
      {totalRows > 0 && (
        <div className="table-footer">
          <span className="table-footer-info">
            Showing {totalRows} {totalRows === 1 ? recordName.replace(/s$/, '').toLowerCase() : recordName.toLowerCase()}
          </span>
        </div>
      )}
    </div>
  );
};

export default GenericTable;
